import { HttpStatusCode } from "axios";
import { TFunction } from "i18next";
import { isString } from "lodash";

import {
  ABORT_ERROR_EXCEPTION_NAME,
  CANCELED_ERROR_EXCEPTION_NAME,
  errors,
  TRANSLATION_KEY,
} from "../constants";
import { createErrorResultFromTranslatableItem, SimpleResult } from "../dto";
import {
  ApiException,
  ILabelItem,
  ITranslatableItem,
  TTranslationParams,
} from "../models";
import { Log } from "./log.helpers";
import { warnAndToastTranslatableItem } from "./translate.helpers";

export const parseDetailedError = (
  t: TFunction,
  error: string,
  errorTranslationKey: string,
): string => {
  return errorTranslationKey ? t(errorTranslationKey) : error;
};

export const isAbortError = (error: unknown): boolean => {
  if (!(error as Error)?.name) {
    return false;
  }

  return (error as Error).name === ABORT_ERROR_EXCEPTION_NAME;
};

export const isCanceledError = (error: unknown): boolean => {
  if (!(error as Error)?.name) {
    return false;
  }

  return (error as Error).name === CANCELED_ERROR_EXCEPTION_NAME;
};

export const isTranslatableItem = (
  item: unknown,
): item is ITranslatableItem => {
  return (
    typeof item === "object" &&
    item !== null &&
    TRANSLATION_KEY in item &&
    isString(item[TRANSLATION_KEY])
  );
};

export const isLabelItem = (error: unknown): error is ILabelItem => {
  return (
    typeof error === "object" &&
    error !== null &&
    "label" in error &&
    isString(error.label)
  );
};

export const toForbiddenTranslatableItem = (
  error: unknown,
  fallbackTranslationKey: string,
): ITranslatableItem => {
  const apiError = error as ApiException;
  if (typeof apiError?.translationKey === "string" && apiError.translationKey) {
    return {
      translationKey: apiError.translationKey,
      translationParams: apiError.translationParams,
    };
  }

  return { translationKey: fallbackTranslationKey };
};

export const extractFirstTranslatableErrorOrFallback = ({
  error,
  fallbackTranslationKey,
  fallbackTranslationParams,
}: {
  error: unknown;
  fallbackTranslationKey: string;
  fallbackTranslationParams?: TTranslationParams;
}): ITranslatableItem => {
  if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
    return toForbiddenTranslatableItem(
      error,
      errors.common.dontHavePermissionForAction,
    );
  }

  const fallbackItem: ITranslatableItem = {
    translationKey: fallbackTranslationKey,
    translationParams: fallbackTranslationParams,
  };

  if (!error) {
    return fallbackItem;
  }

  if (isTranslatableItem(error)) {
    return error;
  }

  if (Array.isArray(error)) {
    const translatableItem = error.find(
      exception => exception && isTranslatableItem(exception),
    );

    if (translatableItem) {
      return translatableItem;
    }
  }

  return fallbackItem;
};

export const simpleResultFromException = <T>({
  error,
  fallbackTranslationKey = errors.common.technicalProblemOccurred,
  fallbackTranslationParams,
}: {
  error: unknown;
  fallbackTranslationKey: string;
  fallbackTranslationParams?: TTranslationParams;
}): SimpleResult<T> => {
  if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
    const errorDetails = toForbiddenTranslatableItem(
      error,
      errors.common.dontHavePermissionForAction,
    );

    return createErrorResultFromTranslatableItem({
      translatableItem: errorDetails,
      debugInfo: error,
    });
  }

  const translatableItem = extractFirstTranslatableErrorOrFallback({
    error,
    fallbackTranslationKey,
    fallbackTranslationParams,
  });

  return createErrorResultFromTranslatableItem({
    translatableItem,
    debugInfo: error,
  });
};

export const warnAndToastException = ({
  error,
  fallbackTranslationKey = errors.common.technicalProblemOccurred,
  fallbackTranslationParams,
  translationFunction,
  logWarnMessage,
}: {
  error: unknown;
  fallbackTranslationKey: string;
  fallbackTranslationParams?: TTranslationParams;
  translationFunction: TFunction<"translation", undefined>;
  logWarnMessage?: string;
}): ITranslatableItem => {
  if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
    const errorDetails = toForbiddenTranslatableItem(
      error,
      errors.common.dontHavePermissionForAction,
    );
    warnAndToastTranslatableItem({
      translatableItem: errorDetails,
      tFunction: translationFunction,
      debugInfo: logWarnMessage,
    });
    return errorDetails;
  }

  const translatableItem = extractFirstTranslatableErrorOrFallback({
    error,
    fallbackTranslationKey,
    fallbackTranslationParams,
  });

  if (!translatableItem) {
    Log.warnAndToast(
      translationFunction(errors.common.technicalProblemOccurred),
      logWarnMessage,
    );
  }

  warnAndToastTranslatableItem({
    translatableItem,
    tFunction: translationFunction,
    debugInfo: logWarnMessage,
  });

  return translatableItem;
};
